import React, { lazy, Suspense, useState, useEffect } from 'react';

import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useSearchParams } from 'react-router-dom';

import { HelmetProvider } from 'react-helmet-async';

import { AuthProvider, useAuth } from './context/AuthContext';

import { CmsProvider } from './cms/CmsContext';

import Header from './components/Header';

import HomePage from './pages/HomePage';

import LoginPage from './pages/LoginPage';

import SignupPage from './pages/SignupPage';

import ForgotPassword from './pages/ForgotPassword';

import ResetPassword from './pages/ResetPassword';

import EventSchedule from './pages/EventSchedule';

import VendorFunnel from './pages/VendorFunnel';

import Sponsors from './pages/Sponsors';

import Contact from './pages/Contact';

import Tickets from './pages/Tickets';

import FestivalTickets from './pages/FestivalTickets';

import Cart from './pages/Cart';

// Checkout page removed — /checkout now redirects to /cart

import Dashboard from './pages/Dashboard';

import UserDashboardV2 from './pages/UserDashboardV2';

import About from './pages/About';

import WhatIsMena from './pages/WhatIsMena';

import FAQ from './pages/FAQ';

import FestivalInfo from './pages/FestivalInfo';

import SponsorsInfo from './pages/SponsorsInfo';

import MapsDirections from './pages/MapsDirections';

import EventMap from './pages/EventMap';

import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';

import CPRANotice from './pages/CPRANotice';

import Accessibility from './pages/Accessibility';

import KeemokaziGiveawayRules from './pages/KeemokaziGiveawayRules';

import TermsOfUse from './pages/TermsOfUse';

import TicketTerms from './pages/TicketTerms';

import BazaarVendorFunnel from './pages/BazaarVendorFunnel';

import FoodVendorFunnel from './pages/FoodVendorFunnel';

import Scanner from './pages/Scanner';

import QrTestPage from './pages/QrTestPage';

import ScannerSystemPresentationPage from './pages/ScannerSystemPresentationPage';
import ScannerSystemPresentationPageV2 from './pages/ScannerSystemPresentationPageV2';

import TicketSuccess from './pages/TicketSuccess';

import SuccessReplica from './pages/SuccessReplica';

import VendorPending from './pages/VendorPending';

import AppDesignStudio from './pages/AppDesignStudio';


import VendorDashboard from './pages/VendorDashboard';

import CoordinatorDashboard from './pages/CoordinatorDashboard';

import CmsLogin from './pages/CmsLogin';

import CmsDashboard from './pages/CmsDashboard';

import GlobalTicketModal from './components/GlobalTicketModal';

import CheckoutV2 from './pages/CheckoutV2';

import TransferAccept from './pages/TransferAccept';

import PublicTicketPage from './pages/PublicTicketPage';

import PublicTicketCollectionPage from './pages/PublicTicketCollectionPage';

import MusicEntertainment from './pages/MusicEntertainment';


import Partners from './pages/Partners';
import Media from './pages/Media';
import Vip from './pages/Vip';
import VipCheckinPage from './pages/VipCheckinPage';
import PostFestivalSurveyPage from './pages/PostFestivalSurveyPage';
// import Hiring from './pages/Hiring'; // hidden for now

import ArabicTextPreview from './pages/ArabicTextPreview';

import PageMetaRouter from './components/PageMetaRouter';

import AmphitheaterTicketsFunnel from './pages/AmphitheaterTicketsFunnel';

// Artist pages
import EhabTawfik from './pages/EhabTawfik';
import Dystinct from './pages/Dystinct';
import MohammedAssaf from './pages/MohammedAssaf';

import StadiumPage from './pages/stadium/StadiumPage';

import Impersonate from './pages/Impersonate';

import PosProductsPage from './pages/PosProductsPage';

import HookahReservation from './pages/HookahReservation';

import HookahSuccess from './pages/HookahSuccess';

import { useAnalyticsPageViews } from './hooks/useAnalyticsPageViews';
import './App.css';
import './pages/AdminDashboard.css';

/* Festival HQ is the admin dashboard — the legacy dashboards stay in the repo
   but are no longer routed; /admin* redirects below send everyone to /hq. */
const FestivalHq = lazy(() => import('./hq/HqApp'));
const FestivalHqRedirect = lazy(() => import('./hq/HqApp').then((m) => ({ default: m.HqRedirect })));
const SolCompanion = lazy(() => import('./sol/SolCompanion'));
const SOL_HIDDEN_PATHS = ['/hq', '/admin', '/cms', '/scanner', '/pos', '/stadium', '/impersonate', '/arabic-tool', '/qr-test', '/scanner-system', '/vip-checkin'];
const isFestivalAdmin = (user) => Boolean(user && (
  user.is_superuser || user.is_admin || String(user.role || '').toUpperCase() === 'ADMIN'
));

const AdminCommerceGate = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AdminRouteFallback />;
  if (!user) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (!isFestivalAdmin(user)) {
    return <section style={{ minHeight: '70vh', display: 'grid', placeItems: 'center', padding: '2rem', textAlign: 'center' }}>
      <div><h1>Admin access only</h1><p>Ticket inventory and purchasing are temporarily restricted to festival administrators.</p><a href="/dashboard">Return to your dashboard</a></div>
    </section>;
  }
  return children;
};

const SolCustomerGate = () => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const hidden = SOL_HIDDEN_PATHS.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`) || (prefix === '/admin' && pathname.startsWith('/admin-')));
  if (loading || !isFestivalAdmin(user) || hidden || isScannerSubdomainHost()) return null;
  return <Suspense fallback={null}><SolCompanion /></Suspense>;
};

const NavigatePreserveSearch = ({ to, replace, state }) => {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  const target = search ? `${to}?${search}` : to;
  return <Navigate to={target} replace={replace} state={state} />;
};

// The four scanner gate subdomains. scan.* is the general gate (unchanged);
// blue/green/pacamp are the three physical festival gates. All four serve the
// same Scanner UI — the gate the scan happened at is derived from the host.
const SCANNER_GATE_BY_HOST = {
  'scan.ocmenafestival.com': 'Scan',
  'blue.ocmenafestival.com': 'Blue',
  'green.ocmenafestival.com': 'Green',
  'pacamp.ocmenafestival.com': 'PAC AMP',
};

const getScannerGateFromHost = () => {
  if (typeof window === 'undefined') return null;
  return SCANNER_GATE_BY_HOST[String(window.location.hostname || '').toLowerCase()] || null;
};

const isScannerSubdomainHost = () => getScannerGateFromHost() !== null;

const RouteChangeHandler = ({ setIsScanPage, setIsCmsPage }) => {

  const location = useLocation();
  const [searchParams] = useSearchParams();

  useAnalyticsPageViews();

  // Capture ?ref=COUPONCODE from any page and persist for auto-apply at cart
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref && ref.trim()) {
      localStorage.setItem('referralCoupon', ref.trim().toUpperCase());
    }
  }, [searchParams]);

  useEffect(() => {

    const isScannerSubdomain = isScannerSubdomainHost();

    window.scrollTo({

      top: 0,

      behavior: 'smooth'

    });

    setIsScanPage(isScannerSubdomain || location.pathname === '/hq' || location.pathname.startsWith('/hq/') || location.pathname === '/scanner' || location.pathname === '/success' || location.pathname === '/admin' || location.pathname === '/admin-v2' || location.pathname === '/admin/preview' || location.pathname === '/admin/app-design' || location.pathname === '/stadium' || location.pathname === '/pos' || location.pathname === '/pos-products' || location.pathname === '/fifa' || location.pathname === '/fifa-success' || location.pathname === '/watch-party' || location.pathname === '/watch-party-success' || location.pathname === '/hookah-lounge' || location.pathname === '/hookah-lounge-success' || location.pathname.startsWith('/hookah-lounge/r/') || location.pathname === '/amphitheater-tickets-saturday' || location.pathname === '/amphitheater-tickets-sunday' || location.pathname === '/arabic-tool' || location.pathname === '/qr-test' || location.pathname === '/scanner-system' || location.pathname === '/vip-checkin' || location.pathname === '/vendor-survey' || location.pathname === '/media-survey' || location.pathname === '/vip-survey' || location.pathname.startsWith('/attendee-survey') || location.pathname.startsWith('/t/') || location.pathname.startsWith('/c/'));

    setIsCmsPage(location.pathname === '/cms' || location.pathname === '/cms-login');

  }, [location.pathname, setIsScanPage, setIsCmsPage]);



  return null;

};



const MAX_FESTIVAL_TICKETS_PER_ORDER = 50;

const AdminRouteFallback = () => <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', color: '#3b6653', fontFamily: 'Arial, sans-serif', fontWeight: 700 }}>Opening admin dashboard…</div>;

const AppWithModal = () => {

  const { user } = useAuth();

  const isScannerSubdomain = isScannerSubdomainHost();

  const scannerSubdomainElement = (
    <Scanner requireStaffAuth={false} showManualSmartScan />
  );

  const [showTicketModal, setShowTicketModal] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);

  const [isScanPage, setIsScanPage] = useState(false);

  const [isCmsPage, setIsCmsPage] = useState(false);

  const [ticketQuantities, setTicketQuantities] = useState({

    '3day': 0,

    '2day': 0,

    '1day': 0

  });



  const handleTicketQuantityChange = (type, value) => {

    const requestedValue = Math.max(0, parseInt(value) || 0);

    setTicketQuantities(prev => {

      const otherTickets = Object.entries(prev).reduce((total, [key, qty]) => {

        return key === type ? total : total + qty;

      }, 0);

      const newValue = Math.min(requestedValue, Math.max(0, MAX_FESTIVAL_TICKETS_PER_ORDER - otherTickets));

      return {

        ...prev,

        [type]: newValue

      };

    });

  };



  const getTotalPrice = () => {

    const prices = { '3day': 35, '2day': 25, '1day': 15 };

    return Object.keys(ticketQuantities).reduce((total, type) => {

      return total + (ticketQuantities[type] * prices[type]);

    }, 0);

  };



  const getTotalTickets = () => {

    return Object.values(ticketQuantities).reduce((total, qty) => total + qty, 0);

  };



  const openTicketModal = () => {

    if (!isFestivalAdmin(user)) {
      window.location.assign('/login?redirect=%2Ffestival-tickets');
      return;
    }
    setShowTicketModal(true);

  };



  const closeTicketModal = () => {

    setShowTicketModal(false);

    setCurrentStep(1);

  };



  return (

    <Router>

      <RouteChangeHandler setIsScanPage={setIsScanPage} setIsCmsPage={setIsCmsPage} />

      <PageMetaRouter />

      <div className="app">

        {!isScanPage && !isCmsPage && <Header onGetTicketsClick={openTicketModal} />}

        <main className="main-content">

          <Routes>

            <Route path="/" element={isScannerSubdomain ? scannerSubdomainElement : <HomePage onGetTicketsClick={openTicketModal} />} />

            <Route path="/login" element={<LoginPage />} />

            <Route path="/signup" element={<SignupPage />} />

            <Route path="/forgot-password" element={<ForgotPassword />} />

            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/event-schedule" element={<EventSchedule />} />

            <Route path="/vendors" element={<AdminCommerceGate><VendorFunnel /></AdminCommerceGate>} />

            <Route path="/sponsors" element={<Sponsors />} />

            <Route path="/contact" element={<Contact />} />

            <Route path="/tickets" element={<AdminCommerceGate><Tickets /></AdminCommerceGate>} />

            <Route path="/festival-tickets" element={<AdminCommerceGate><FestivalTickets /></AdminCommerceGate>} />

            {/* Hookah Lounge reservations — canonical slug mirrors watch-party style. */}
            <Route path="/hookah-lounge" element={<HookahReservation />} />
            <Route path="/hookah-lounge-success" element={<HookahSuccess />} />
            <Route path="/hookah-lounge/r/:token" element={<HookahSuccess /> /* email CTA lands here */} />

            {/* Explicit self-service aliases */}
            <Route path="/self-service/hookah-lounge" element={<NavigatePreserveSearch to="/hookah-lounge" replace />} />
            <Route path="/self-service/hookah-lounge-success" element={<NavigatePreserveSearch to="/hookah-lounge-success" replace />} />

            {/* Backward-compatible aliases */}
            <Route path="/hookah" element={<NavigatePreserveSearch to="/hookah-lounge" replace />} />
            <Route path="/hookah-success" element={<NavigatePreserveSearch to="/hookah-lounge-success" replace />} />
            <Route path="/hookah/r/:token" element={<HookahSuccess />} />

            <Route path="/amphitheater-tickets" element={<AdminCommerceGate><AmphitheaterTicketsFunnel /></AdminCommerceGate>} />

            <Route path="/amphitheater-tickets-saturday" element={<AdminCommerceGate><StadiumPage forcedDay="saturday" hideDaySwitcher /></AdminCommerceGate>} />

            <Route path="/amphitheater-tickets-sunday" element={<AdminCommerceGate><StadiumPage forcedDay="sunday" hideDaySwitcher /></AdminCommerceGate>} />


            <Route path="/checkout-v2" element={<AdminCommerceGate><CheckoutV2 /></AdminCommerceGate>} />

            <Route path="/cart" element={<AdminCommerceGate><Cart /></AdminCommerceGate>} />

            <Route path="/checkout" element={<AdminCommerceGate><NavigatePreserveSearch to="/cart" replace /></AdminCommerceGate>} />

            <Route path="/dashboard" element={<UserDashboardV2 />} />

            <Route path="/dashboard-classic" element={<Dashboard />} />

            <Route path="/about" element={<About />} />

            <Route path="/what-is-mena" element={<WhatIsMena />} />

            <Route path="/faq" element={<FAQ />} />

            <Route path="/festival" element={<FestivalInfo />} />

            <Route path="/festival-info" element={<NavigatePreserveSearch to="/festival" replace />} />

            <Route path="/music-entertainment" element={<MusicEntertainment />} />

            <Route path="/music" element={<NavigatePreserveSearch to="/music-entertainment" replace />} />

            {/* Artist pages */}
            <Route path="/mohammed-assaf" element={<MohammedAssaf />} />
            <Route path="/dystinct" element={<Dystinct />} />
            <Route path="/ehab-tawfik" element={<EhabTawfik />} />

            <Route path="/partners" element={<Partners />} />

            <Route path="/media" element={<Media />} />

            <Route path="/vip" element={<Vip />} />
            <Route path="/vip-checkin" element={<VipCheckinPage />} />
            <Route path="/vendor-survey" element={<PostFestivalSurveyPage surveyType="vendor" />} />
            <Route path="/media-survey" element={<PostFestivalSurveyPage surveyType="media" />} />
            <Route path="/vip-survey" element={<PostFestivalSurveyPage surveyType="vip" />} />
            <Route path="/attendee-survey-sat" element={<PostFestivalSurveyPage surveyType="attendee_sat" backendSurveyType="attendee" />} />
            <Route path="/attendee-survey-sun" element={<PostFestivalSurveyPage surveyType="attendee_sun" backendSurveyType="attendee" />} />
            <Route path="/attendee-survey-both" element={<PostFestivalSurveyPage surveyType="attendee_both" backendSurveyType="attendee" />} />
            <Route path="/attendee-survey-festival" element={<PostFestivalSurveyPage surveyType="attendee_festival" backendSurveyType="attendee" />} />
            <Route path="/attendee-survey" element={<PostFestivalSurveyPage surveyType="attendee_festival" backendSurveyType="attendee" />} />

            {/* /hiring temporarily hidden — re-enable when ready */}
            {/* <Route path="/hiring" element={<Hiring />} /> */}

            <Route path="/sponsors-info" element={<SponsorsInfo />} />

            <Route path="/maps-directions" element={<MapsDirections />} />

            <Route path="/event-map" element={<EventMap />} />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            <Route path="/delete-account" element={<DeleteAccount />} />

            <Route path="/cpra-notice" element={<CPRANotice />} />

            <Route path="/accessibility" element={<Accessibility />} />

            <Route path="/keemokazi-giveaway-rules" element={<KeemokaziGiveawayRules />} />

            <Route path="/terms-of-use" element={<TermsOfUse />} />

            <Route path="/ticket-terms" element={<TicketTerms />} />

            <Route path="/vendor-booths" element={<NavigatePreserveSearch to="/vendors" replace />} />

            <Route path="/about-vendors" element={<NavigatePreserveSearch to="/vendors" replace />} />

            <Route path="/bazaar-vendor" element={<AdminCommerceGate><BazaarVendorFunnel /></AdminCommerceGate>} />

            <Route path="/food-vendor" element={<AdminCommerceGate><FoodVendorFunnel /></AdminCommerceGate>} />

            <Route path="/scanner" element={isScannerSubdomain ? scannerSubdomainElement : <Scanner />} />

            <Route path="/qr-test" element={<QrTestPage />} />

            <Route path="/scanner-system" element={<ScannerSystemPresentationPageV2 />} />

            <Route path="/scanner-system-v1" element={<ScannerSystemPresentationPage />} />

            <Route path="/success" element={<TicketSuccess />} />

            <Route path="/order-success" element={<SuccessReplica />} />

            <Route path="/app" element={<VendorPending />} />

            <Route path="/admin" element={<Navigate to="/hq" replace />} />
            <Route path="/admin-v2" element={<Navigate to="/hq" replace />} />
            <Route path="/admin/preview" element={<Navigate to="/hq" replace />} />

            <Route path="/admin/app-design" element={<AppDesignStudio />} />

            <Route path="/hq" element={<Suspense fallback={<AdminRouteFallback />}><FestivalHqRedirect /></Suspense>} />
            <Route path="/hq/:tab" element={<Suspense fallback={<AdminRouteFallback />}><FestivalHq /></Suspense>} />


            <Route path="/vendor-dashboard" element={<VendorDashboard />} />

            <Route path="/coordinator" element={<CoordinatorDashboard />} />

            <Route path="/transfer/accept" element={<TransferAccept />} />

            <Route path="/t/:ticketCode" element={<PublicTicketPage />} />

            <Route path="/c/:token" element={<PublicTicketCollectionPage />} />

            <Route path="/cms-login" element={<CmsLogin />} />

            <Route path="/cms" element={<CmsDashboard />} />

            <Route path="/stadium" element={<StadiumPage />} />

            <Route path="/impersonate" element={<Impersonate />} />

            <Route path="/arabic-tool" element={<ArabicTextPreview />} />

            <Route path="/pos" element={<PosProductsPage />} />

            <Route path="/pos-products" element={<PosProductsPage />} />

            <Route path="*" element={<NavigatePreserveSearch to="/" replace />} />

          </Routes>

        </main>

      </div>



      <SolCustomerGate />

      {showTicketModal && (

        <GlobalTicketModal

          isOpen={showTicketModal}

          onClose={closeTicketModal}

          currentStep={currentStep}

          setCurrentStep={setCurrentStep}

          ticketQuantities={ticketQuantities}

          handleTicketQuantityChange={handleTicketQuantityChange}

          getTotalPrice={getTotalPrice}

          getTotalTickets={getTotalTickets}

        />

      )}

    </Router>

  );

};



function App() {

  return (

    <HelmetProvider>

      <AuthProvider>

        <CmsProvider>

          <AppWithModal />

        </CmsProvider>

      </AuthProvider>

    </HelmetProvider>

  );

}



export default App;

